import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import { List, type RowComponentProps } from "react-window";
import dayjs from "dayjs";
import { useAuth } from "@/context/AuthContext";
import { getUserCompanies } from "@/api/user.service";
import type { Company } from "@/api/company.service";
import { formatCurrency } from "@/utils/format";
import { CrudPage } from "@/components/crud/CrudPage";
import {
  getListProducts,
  getProductDetail,
  getPriceDetail,
  getDailySale,
  getPromotionHistoric,
  getDailyCust,
  getDailyStock,
  getStockTransit,
  type Product,
  type ProductCompanyDetail,
  type DetailTableParams,
} from "@/api/products.service";
import { alpha } from "@mui/material/styles";

interface DetailColumn {
  key: string;
  label: string;
  format?: "currency" | "date";
}

interface DetailConfig {
  title: string;
  columns: DetailColumn[];
  fetch: (params: DetailTableParams, clientId: string) => Promise<any[]>;
}

const DETAIL_CONFIGS: Record<string, DetailConfig> = {
  detalhe_preco: {
    title: "Histórico de preço",
    columns: [
      { key: "embalagem", label: "Emb." },
      { key: "preco_embalagem", label: "Pr. emb.", format: "currency" },
      { key: "preco_unidade", label: "Pr. unid.", format: "currency" },
      { key: "margem", label: "Margem" },
    ],
    fetch: getPriceDetail,
  },
  detalhe_mdv: {
    title: "Vendas por dia",
    columns: [
      { key: "data", label: "Dia", format: "date" },
      { key: "qtd", label: "Qtd" },
      { key: "qtd_inicial", label: "Est. inicial" },
    ],
    fetch: getDailySale,
  },
  detalhe_situacao_vigente: {
    title: "Histórico de promoções",
    columns: [
      { key: "dtainicio", label: "Início", format: "date" },
      { key: "dtafim", label: "Fim", format: "date" },
      { key: "embalagem", label: "Emb." },
      { key: "preco", label: "Preço", format: "currency" },
    ],
    fetch: getPromotionHistoric,
  },
  detalhe_custo_liquido: {
    title: "Custo por dia",
    columns: [
      { key: "dtaentradasaida", label: "Dia", format: "date" },
      { key: "estqinicial", label: "Est. inicial" },
      { key: "estqfinal", label: "Est. final" },
      { key: "custo", label: "Custo", format: "currency" },
    ],
    fetch: getDailyCust,
  },
  detalhe_estoque_disponivel: {
    title: "Estoque por dia",
    columns: [
      { key: "dtaentradasaida", label: "Dia", format: "date" },
      { key: "qtdentrada", label: "Qtd. ent." },
      { key: "qtdsaida", label: "Qtd. saí." },
      { key: "qtdsaldo", label: "Saldo" },
    ],
    fetch: getDailyStock,
  },
  detalhe_estoque_transito: {
    title: "Estoque em trânsito",
    columns: [
      { key: "numeronf", label: "NF" },
      { key: "serienf", label: "Série" },
      { key: "dtaemissao", label: "Data", format: "date" },
      { key: "quantidade", label: "Qtd." },
    ],
    fetch: getStockTransit,
  },
};

const ROW_HEIGHT = 72;
const LIST_HEIGHT = 560;

function ProductRow({
  index,
  style,
  products,
  onSelect,
}: RowComponentProps<{ products: Product[]; onSelect: (p: Product) => void }>) {
  const p = products[index];
  if (!p) return null;

  return (
    <div style={{ ...style, paddingBottom: 10, boxSizing: "border-box" }}>
      <Box
        onClick={() => onSelect(p)}
        sx={{
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2.5,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          cursor: "pointer",
          transition: "box-shadow .15s, border-color .15s",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ textTransform: "uppercase" }} noWrap>
            {p.title}
          </Typography>
          <Typography variant="caption" sx={{ color: "grey.400", fontWeight: 700, letterSpacing: "0.5px" }}>
            COD. {p.code}
          </Typography>
        </Box>
        <ChevronRight sx={{ color: "grey.400", flexShrink: 0 }} />
      </Box>
    </div>
  );
}

function formatCell(value: unknown, format?: DetailColumn["format"]) {
  if (value === null || value === undefined) return "—";
  if (format === "currency") return formatCurrency(Number(value));
  if (format === "date") return dayjs(String(value)).format("DD/MM/YYYY");
  return String(value);
}

function DetailDialog({
  entrypoint,
  params,
  clientId,
  onClose,
}: {
  entrypoint: string;
  params: DetailTableParams;
  clientId: string;
  onClose: () => void;
}) {
  const config = DETAIL_CONFIGS[entrypoint];
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    setLoading(true);
    config
      .fetch(params, clientId)
      .then(setRows)
      .finally(() => setLoading(false));
  }, [entrypoint, params.codproduto, params.nrempresa, clientId]);

  if (!config) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{config.title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <LinearProgress />
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            Não há informações para apresentar.
          </Typography>
        ) : (
          <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.subtle" }}>
                  {config.columns.map((col) => (
                    <TableCell key={col.key} sx={{ fontWeight: 700, color: "grey.400", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    {config.columns.map((col) => (
                      <TableCell key={col.key}>{formatCell(row[col.key], col.format)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProductDetailView({ product, clientId, companies }: { product: Product; clientId: string; companies: Company[] }) {
  const [details, setDetails] = useState<ProductCompanyDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeStore, setActiveStore] = useState(0);
  const [openDetail, setOpenDetail] = useState<{ entrypoint: string; companyErp: string } | null>(null);

  useEffect(() => {
    setLoading(true);
    getProductDetail(product.code, clientId)
      .then(setDetails)
      .finally(() => setLoading(false));
  }, [product.code, clientId]);

  if (loading) return <LinearProgress />;
  if (details.length === 0) return <Typography color="text.secondary">Sem detalhes para este produto.</Typography>;

  const store = details[activeStore];
  if (!store) return null;

  const storeName = companies.find((c) => c.erp_code === store.companyErp)?.name ?? store.companyErp;

  return (
    <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", p: { xs: 2, sm: 3 } }}>
      <Tabs
        value={activeStore}
        onChange={(_, v) => setActiveStore(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        {details.map((d, idx) => (
          <Tab key={idx} label={companies.find((c) => c.erp_code === d.companyErp)?.name ?? d.companyErp} />
        ))}
      </Tabs>

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {storeName} {store.category ? `· ${store.category}` : ""}
      </Typography>

      <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
        {store.sections.map((section, idx) => (
          <Stack
            key={idx}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              py: 1.5,
              px: section.entrypoint ? 1 : 0,
              mx: section.entrypoint ? -1 : 0,
              borderRadius: 2,
              cursor: section.entrypoint ? "pointer" : "default",
              transition: "background-color .15s",
              "&:hover": section.entrypoint ? { bgcolor: "background.subtle" } : undefined,
            }}
            onClick={() => section.entrypoint && setOpenDetail({ entrypoint: section.entrypoint, companyErp: store.companyErp })}
          >
            <Typography color="text.secondary">{section.label}</Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography fontWeight={700} sx={{ color: section.entrypoint ? "primary.main" : "text.primary" }}>
                {section.value}
              </Typography>
              {section.entrypoint && <ChevronRight fontSize="small" sx={{ color: "primary.main" }} />}
            </Stack>
          </Stack>
        ))}
      </Stack>

      {(store.lastEntry || store.lastSale) && (
        <Stack direction="row" gap={4} sx={{ mt: 2, p: 2, bgcolor: "background.subtle", borderRadius: 2 }}>
          {store.lastEntry && (
            <Box>
              <Typography variant="caption" color="text.secondary">ÚLTIMA ENTRADA</Typography>
              <Typography fontWeight={600}>{dayjs(store.lastEntry).format("DD/MM/YYYY")}</Typography>
            </Box>
          )}
          {store.lastSale && (
            <Box>
              <Typography variant="caption" color="text.secondary">ÚLTIMA VENDA</Typography>
              <Typography fontWeight={600}>{dayjs(store.lastSale).format("DD/MM/YYYY")}</Typography>
            </Box>
          )}
        </Stack>
      )}

      {openDetail && (
        <DetailDialog
          entrypoint={openDetail.entrypoint}
          params={{ codproduto: product.code, nrempresa: openDetail.companyErp }}
          clientId={clientId}
          onClose={() => setOpenDetail(null)}
        />
      )}
    </Card>
  );
}

export function ConsultaProdutoPage() {
  const { user } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const clientId = user?.loggedClient?.id;

  useEffect(() => {
    if (!user) return;
    getUserCompanies(user.id).then((data) => setCompanies(data.filter((c) => c.client_id === clientId)));
  }, [user, clientId]);

  useEffect(() => {
    if (!clientId) return;
    const text = searchText.trim();
    if (!text) {
      setProducts([]);
      return;
    }

    const debounce = setTimeout(async () => {
      const isTypedBarcode = /^\d{8,}$/.test(text);
      setLoading(true);
      try {
        const response = await getListProducts(
          { filtro: isTypedBarcode ? "" : text, ean: isTypedBarcode ? text : "" },
          clientId,
        );
        setProducts(response);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchText, clientId]);

  if (!clientId) {
    return (
      <CrudPage title="Consulta de Produtos">
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Selecione um cliente para consultar produtos.
        </Typography>
      </CrudPage>
    );
  }

  return (
    <CrudPage title="Consulta de Produtos" subtitle="Pesquise por nome ou código de barras para ver preço, estoque e histórico">
      <Stack gap={2.5}>
        <TextField
          placeholder="Nome ou código de barras do produto..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search /></InputAdornment> } }}
        />

        {loading && <LinearProgress />}

        {!selectedProduct ? (
          <>
            {!loading && searchText.trim() && products.length === 0 && (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Nenhum produto encontrado.
              </Typography>
            )}
            {products.length > 0 && (
              <List
                rowComponent={ProductRow}
                rowCount={products.length}
                rowHeight={ROW_HEIGHT}
                rowProps={{ products, onSelect: setSelectedProduct }}
                style={{ height: LIST_HEIGHT }}
              />
            )}
          </>
        ) : (
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              onClick={() => setSelectedProduct(null)}
              sx={{ cursor: "pointer", mb: 2, width: "fit-content", color: "primary.main" }}
            >
              <ChevronLeft fontSize="small" />
              <Typography variant="body2" fontWeight={600}>Voltar à busca</Typography>
            </Stack>
            <ProductDetailView product={selectedProduct} clientId={clientId} companies={companies} />
          </Box>
        )}
      </Stack>
    </CrudPage>
  );
}
